import { BRAND_OG_IMAGE_URL, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { getHomepageSeoCopy } from "@/lib/brandSeoCopy";
import { BaseTemplateBlack as BaseTemplate } from "@/components/templates/BaseTemplateBlack";
import { BaseTemplate as BaseTemplateRich } from "@/components/templates/BaseTemplate";
import { BaseTemplateInternational } from "@/components/templates/BaseTemplateInternational";
import { BioLinkTemplate } from "@/components/templates/BioLinkTemplate";
import { ArgentinaBioTemplate } from "@/components/templates/ArgentinaBioTemplate";
import { MaintenanceTemplate } from "@/components/templates/MaintenanceTemplate";
import BioTemplatePremium from "@/components/templates/BioTemplatePremium";
// BioTemplatePremiumSimple deprecado 2026-06-14 — replaced por DualCTAVideoLanding.
// Mantenemos el import comentado para hacer rollback trivial si hace falta.
// import BioTemplatePremiumSimple from "@/components/templates/BioTemplatePremiumSimple";
import DualCTAVideoLanding from "@/pages/DualCTAVideoLanding";
import {
  getCurrencyForCountry,
  getPlansForCurrency,
  isPremiumSpecialOfferActive,
} from "@/config/bioTemplatePremiumPlans";
import {
  getPremiumInvitePaths,
  PREMIUM_INVITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { isCloudinaryImage, isSameCloudinaryImage } from "@/lib/cloudinaryUtils";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useSEO } from "@/hooks/useSEO";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { usePremiumInviteFreeTelegramPublicUrl } from "@/hooks/usePremiumInviteFreeTelegramPublicUrl";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  BESTFRIEND_INSTAGRAM_CONFIG,
  getBestFriendInstagramConfigForCountry,
} from "@/config/bestFriendInstagramConfig";
import { getGalleryPhotos } from "@/config/galleryConfig";
import { useEffect, useMemo, useState } from "react";

// Development components
import { ArgentinaProvinceAdmin } from "@/components/dev/ArgentinaProvinceAdmin";

export const Index = () => {
  const templateSelection = useTemplateSelection();
  const { templateName, country, language } = templateSelection;
  const { setTemplateName } = useCurrentTemplate();

  const premiumSimpleHome = templateName === "BioTemplatePremiumSimple";
  const {
    url: premiumSimpleFreeTelegramUrl,
    publicConfig: premiumInvitePublicConfig,
    ready: premiumSimpleConfigReady,
  } = usePremiumInviteFreeTelegramPublicUrl(premiumSimpleHome);

  // PostHog + session replay: expose template for pageview/route_change (all templates)
  useEffect(() => {
    setTemplateName(templateName ?? null);
    return () => setTemplateName(null);
  }, [templateName, setTemplateName]);

  // Site settings from admin (BaseTemplate: header URL, title, description, gallery)
  const { settings: siteSettings, isLoading: siteSettingsLoading } = useSiteSettings();
  // Portfolio sections from admin - solo para ArgentinaBioTemplate/BioLinkTemplate (BaseTemplate usa solo site settings)
  const {
    allImageUrls: portfolioImageUrls,
    isLoading: portfolioLoading,
    showPortfolioNavLink,
  } = usePortfolioSections();

  // Timeout de seguridad: si después de 8 segundos aún está cargando, forzar render
  const [forceRender, setForceRender] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (siteSettingsLoading || portfolioLoading) {
        console.warn('[Index] Loading timeout reached, forcing render with defaults');
        setForceRender(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [siteSettingsLoading, portfolioLoading]);

  // SEO home: tono profesional LATAM por defecto (sin Telegram / premium en meta).
  // BaseTemplate puede sobreescribir con site-settings del admin.
  const isBaseTemplate = !["ArgentinaBioTemplate", "BioLinkTemplate", "MaintenanceTemplate", "BioTemplatePremium", "BioTemplatePremiumSimple", "DualCTAVideoLanding"].includes(templateName ?? "");
  const brandSeo = getHomepageSeoCopy("es");
  const seoConfig = {
    title: isBaseTemplate && siteSettings.title?.trim()
      ? siteSettings.title
      : brandSeo.title,
    description: isBaseTemplate && siteSettings.description?.trim()
      ? siteSettings.description
      : brandSeo.description,
    keywords: brandSeo.keywords,
    ogImage: BRAND_OG_IMAGE_URL,
    ogTitle: isBaseTemplate && siteSettings.title?.trim()
      ? siteSettings.title
      : brandSeo.ogTitle,
    ogDescription: isBaseTemplate && siteSettings.description?.trim()
      ? siteSettings.description
      : brandSeo.ogDescription,
    canonical: `${BRAND_SITE_URL}/`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
    type: "website",
    locale: brandSeo.htmlLocale,
    alternateLocale: "en_US",
    // Homepage: index, follow for crawlers (BaseTemplate and other public templates)
    robots: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
  };

  useSEO(seoConfig);

  // Workaround: VITE_GALLERY_PHOTOS (comma-separated URLs) alimenta la galería si está definido
  const envGalleryPhotoItems = useMemo(
    () =>
      getGalleryPhotos().map((item, i) => ({
        id: i + 1,
        type: "photo" as const,
        src: item.src.trim(),
        title: "",
      })),
    []
  );
  const useEnvGalleryPhotos = envGalleryPhotoItems.length > 0;

  // Gallery: admin es la autoridad (portfolio → site settings); VITE_GALLERY_PHOTOS es solo fallback
  const siteGalleryUrls = (siteSettings.galleryImageUrls ?? [])
    .filter((u): u is string => typeof u === "string" && u.trim().length > 0 && isCloudinaryImage(u))
    .map((u) => u.trim());
  const logoUrl = (siteSettings.logoUrl as string)?.trim() || "";
  const galleryWithoutLogo = logoUrl
    ? siteGalleryUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : siteGalleryUrls;

  const portfolioUrls = portfolioImageUrls
    .filter((u) => u?.trim() && isCloudinaryImage(u))
    .map((u) => u.trim());
  const portfolioWithoutLogo = logoUrl
    ? portfolioUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : portfolioUrls;

  // Galería para ArgentinaBioTemplate/BioLinkTemplate: admin (portfolio → site settings)
  // es la autoridad; VITE_GALLERY_PHOTOS pasa a ser SOLO fallback si la DB está vacía.
  const galleryMediaItems = !portfolioLoading && portfolioWithoutLogo.length > 0
    ? (portfolioWithoutLogo.length > 1 ? portfolioWithoutLogo.slice(1) : portfolioWithoutLogo).map((src, i) => ({
        id: i + 1,
        type: "photo" as const,
        src: src.trim(),
        title: "",
      }))
    : galleryWithoutLogo.length > 0
      ? (galleryWithoutLogo.length > 1 ? galleryWithoutLogo.slice(1) : galleryWithoutLogo).map((src, i) => ({
          id: i + 1,
          type: "photo" as const,
          src: src.trim(),
          title: "",
        }))
      : !portfolioLoading && useEnvGalleryPhotos
        ? envGalleryPhotoItems
        : [];

  // Política home: AR → BaseTemplate; intl → DualCTAVideoLanding (Mux + Stripe).
  // El Worker es autoridad; esto es defensa en cliente ante config stale.
  const policyTemplateName = useMemo(() => {
    const cc = (country || "UNKNOWN").toUpperCase();
    if (cc === "AR") {
      return templateName === "DualCTAVideoLanding" ? "BaseTemplate" : templateName;
    }
    if (cc !== "UNKNOWN") {
      // Override por país (CountryTemplateAdmin): un país no-AR puede elegir
      // BaseTemplateInternational en vez del DEFAULT DualCTAVideoLanding. El
      // Worker es autoridad; honramos su elección explícita de Base.
      if (
        templateName === "BaseTemplateInternational" ||
        templateName === "BaseTemplate"
      ) {
        return templateName;
      }
      if (templateName === "BioTemplatePremiumSimple") return templateName;
      return "DualCTAVideoLanding";
    }
    return templateName;
  }, [country, templateName]);

  const renderTemplate = () => {
    if (policyTemplateName === "DualCTAVideoLanding") {
      return <DualCTAVideoLanding />;
    }

    if (policyTemplateName === "ArgentinaBioTemplate") {
      return <ArgentinaBioTemplate mediaItems={galleryMediaItems} />;
    }

    if (policyTemplateName === "BioLinkTemplate") {
      return <BioLinkTemplate mediaItems={galleryMediaItems} />;
    }

    if (policyTemplateName === "MaintenanceTemplate") {
      return <MaintenanceTemplate />;
    }

    // Override por país a BaseTemplateInternational (home editorial intl). Explícito
    // porque para no-AR `useInternationalLayout` puede venir false (showWishlist se
    // suprime en el exterior), y sin esto caería a <BaseTemplate> (español).
    if (policyTemplateName === "BaseTemplateInternational") {
      const baseTemplateLoading = !forceRender && siteSettingsLoading;
      return (
        <BaseTemplateInternational
          mediaItems={[]}
          bannerDeferred={baseTemplateLoading}
          galleryDeferred={baseTemplateLoading}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
        />
      );
    }

    // Override por país a BaseTemplate (home editorial RICA — misma que /colombia,
    // /bogota, etc.: galería + hero CTA Stripe vía resolveHeroCtaForBaseTemplate).
    // Solo no-AR: AR usa BaseTemplateBlack (fallthrough de abajo), no este componente.
    if (
      policyTemplateName === "BaseTemplate" &&
      (country || "").toUpperCase() !== "AR"
    ) {
      const baseTemplateLoading = !forceRender && siteSettingsLoading;
      return (
        <BaseTemplateRich
          mediaItems={[]}
          bannerDeferred={baseTemplateLoading}
          galleryDeferred={baseTemplateLoading}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
          showGalleryNavLink
          showContactNavLink
          showGallerySection
          showTallySection
        />
      );
    }

    if (policyTemplateName === "BioTemplatePremium") {
      const currency = getCurrencyForCountry(country);
      const { standardPlan, vipPlan } = getPlansForCurrency(currency, language);
      const { successPath, cancelPath } = getPremiumInvitePaths(currency);
      const bestfriendInstagram =
        getBestFriendInstagramConfigForCountry(country) ??
        (isPremiumSpecialOfferActive() ? BESTFRIEND_INSTAGRAM_CONFIG : null);

      return (
        <BioTemplatePremium
          currency={currency}
          country={country}
          language={language}
          standardPlan={standardPlan}
          vipPlan={vipPlan}
        successPath={successPath}
        cancelPath={cancelPath}
        profileName={BRAND_PERSON_NAME}
        flowName={PREMIUM_INVITE_FLOW_NAME}
        bestfriendInstagram={bestfriendInstagram ?? undefined}
        />
      );
    }

    if (policyTemplateName === "BioTemplatePremiumSimple") {
      // Deprecación 2026-06-14: BioTemplatePremiumSimple fue reemplazado por
      // DualCTAVideoLanding para coherencia con la home. Si el Worker o el
      // admin todavía resuelven a "BioTemplatePremiumSimple", lo redirigimos
      // visualmente al template nuevo. DualCTAVideoLanding es self-contained
      // (lee config admin internamente) y no necesita las props legacy de
      // language/country/freeTelegramPublicUrl/premiumInvitePublicConfig.
      return <DualCTAVideoLanding />;
    }

    // BaseTemplate family: autoridad en el template (Worker KV + useSiteSettings + brand defaults).
    const baseTemplateLoading = !forceRender && siteSettingsLoading;
    const baseTemplateShellProps = {
      mediaItems: [] as { id: number; type: "photo"; src: string; title: string }[],
      bannerDeferred: baseTemplateLoading,
      galleryDeferred: baseTemplateLoading,
      showSobreMiOnHome: false as const,
      showPortfolioNavLink,
    };

    // Worker = autoridad: decide BaseTemplate vs BaseTemplateInternational vía
    // `useInternationalLayout` en __TEMPLATE_CONFIG__. El cliente no re-deriva geo
    // (antes mezclaba usesSpanishDefaultForSite + country + showWishlist y podía
    // discrepar del Worker si una cookie stale forzaba bucket AR).
    if (getServerTemplateConfig()?.useInternationalLayout === true) {
      return <BaseTemplateInternational {...baseTemplateShellProps} />;
    }

    return <BaseTemplate {...baseTemplateShellProps} />;
  };

  // Render template immediately - no loading screen
  return (
    <>
      {renderTemplate()}
      <ArgentinaProvinceAdmin />
    </>
  );
};

export default Index;